import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const TermsOfService = () => {
  const tableOfContents = [
    { id: 'our-role', title: 'Our Role and Services' },
    { id: 'no-advice', title: 'No Professional Advice' },
    { id: 'accounts', title: 'User Accounts and Responsibilities' },
    { id: 'acceptable-use', title: 'Acceptable Use' },
    { id: 'third-party', title: 'Third-Party Services' },
    { id: 'disclaimers', title: 'Disclaimers and Limitations' },
    { id: 'dispute-resolution', title: 'Dispute Resolution' },
    { id: 'communications', title: 'Electronic Communications' },
    { id: 'termination', title: 'Termination' },
    { id: 'changes', title: 'Changes to Terms' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Sticky on desktop */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
              <p className="text-muted-foreground mb-8">Last updated: January 2024</p>
              
              <div className="prose prose-lg max-w-none space-y-8">
                <div className="text-muted-foreground leading-relaxed">
                  <p className="mb-6">
                    These Terms of Service govern your use of Safurra's website and services. By using our services, you agree to these terms.
                  </p>
                </div>

                <section id="our-role">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Our Role and Services</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p><strong className="text-foreground">Important:</strong> Safurra is not an insurance company and does not issue policies. We are a technology platform that helps consumers compare life insurance options and connect with licensed carriers.</p>
                    <p>Final rates, terms, coverage details, and eligibility are determined solely by the insurance carrier. We provide quotes and referrals and may help complete applications with carriers, but we do not guarantee coverage or final pricing.</p>
                  </div>
                </section>

                <section id="no-advice">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">No Professional Advice</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>Information on our website and through our services is for educational and informational purposes only. It does not constitute legal, financial, tax, medical, or professional advice.</p>
                    <p>You should consult with qualified professionals regarding your specific insurance needs and financial situation.</p>
                  </div>
                </section>

                <section id="accounts">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">User Accounts and Responsibilities</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>When creating an account, you must:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide accurate, current, and complete information</li>
                      <li>Maintain and update your information as needed</li>
                      <li>Keep your account credentials secure</li>
                      <li>Notify us immediately of any unauthorized use</li>
                      <li>Be responsible for all activities under your account</li>
                    </ul>
                  </div>
                </section>

                <section id="acceptable-use">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Acceptable Use</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>You may not use our services to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Violate any laws or regulations</li>
                      <li>Provide false or misleading information</li>
                      <li>Interfere with or disrupt our services</li>
                      <li>Attempt to gain unauthorized access to our systems</li>
                      <li>Upload malicious code or harmful content</li>
                      <li>Engage in fraudulent activities</li>
                      <li>Harass, abuse, or harm others</li>
                    </ul>
                  </div>
                </section>

                <section id="third-party">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Third-Party Services</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>Our platform integrates with third-party services including insurance carriers, payment processors, and identity verification providers. We are not responsible for the availability, accuracy, or content of these third-party services.</p>
                    <p>Your use of third-party services is subject to their own terms and privacy policies.</p>
                  </div>
                </section>

                <section id="disclaimers">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Disclaimers and Limitations</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p><strong className="text-foreground">Warranty Disclaimer:</strong> Our services are provided "as is" without warranties of any kind, express or implied.</p>
                    <p><strong className="text-foreground">Limitation of Liability:</strong> To the fullest extent permitted by law, Safurra shall not be liable for any indirect, incidental, special, or consequential damages.</p>
                    <p><strong className="text-foreground">Indemnification:</strong> You agree to indemnify and hold harmless Safurra from any claims arising out of your use of our services or violation of these terms.</p>
                  </div>
                </section>

                <section id="dispute-resolution">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Dispute Resolution</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p><strong className="text-foreground">Binding Arbitration:</strong> Any disputes arising out of or relating to these Terms or our services shall be resolved through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association.</p>
                    <p><strong className="text-foreground">Class Action Waiver:</strong> You agree that any arbitration shall be conducted in your individual capacity only and not as a class action or other representative action.</p>
                    <p><strong className="text-foreground">Opt-Out:</strong> You may opt out of this arbitration agreement by sending written notice to <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a> within 30 days of accepting these Terms.</p>
                    <p><strong className="text-foreground">Governing Law:</strong> These Terms are governed by the laws of the State of California.</p>
                  </div>
                </section>

                <section id="communications">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Electronic Communications</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>By using our services, you consent to receive electronic communications from us, including emails, text messages, and in-app notifications.</p>
                    <p><strong className="text-foreground">E-SIGN Consent:</strong> You agree that electronic signatures, contracts, and documents have the same legal effect as physical documents.</p>
                    <p><strong className="text-foreground">TCPA Consent:</strong> If you provide a phone number, you consent to receive SMS messages related to your quotes and account. Message and data rates may apply.</p>
                  </div>
                </section>

                <section id="termination">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Termination</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>We may terminate or suspend your account and access to our services at any time, with or without cause or notice.</p>
                    <p>Upon termination, certain provisions of these Terms will survive, including disclaimers, limitations of liability, indemnification, and dispute resolution.</p>
                  </div>
                </section>

                <section id="changes">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Changes to Terms</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last updated" date.</p>
                    <p>Your continued use of our services after changes become effective constitutes acceptance of the new Terms.</p>
                  </div>
                </section>

                <div className="mt-8 p-6 bg-muted rounded-lg">
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Contact Us:</strong> If you have questions about these Terms, please contact us at <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TermsOfService;